import React, { useEffect, useRef, useState } from 'react';
import { timeDay, timeMonday, timeSunday } from 'd3-time';
import { timeFormat, timeFormatDefaultLocale } from 'd3-time-format';
import { useTranslation } from 'react-i18next';

import { mmolL, mgdL } from './utils/consts';
import { dateNames, formatGlucose, formatGlucoseUnits, formatGlucoseValue, parseDate } from './utils/formats';
import { initialLocale } from './utils/config';

export const dayTimeFormat = timeFormat('%H:%M');

const initialLocaleDefinition = {
  dateTime: '%x, %X',
  shortDateTime: '%-m/%-d/%Y at %X',
  date: '%-m/%-d/%Y',
  time: '%-I:%M:%S %p',
  periods: ['AM', 'PM'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdayFormat: '%A %-m/%-d',
  fullHourFormat: '%-I %p',
  week: '%w',
  clockFormat: '%-I:%M %p',
};

const defaultUnits = mmolL;

const dateFormat = timeFormat('%x');

function formatDateString(s) {
  return dateFormat(parseDate(s));
}

function formatDateRange(dateStrings, dateFormatter = formatDateString) {
  let formattedDateRange;
  if (timeDay.count(parseDate(dateStrings[0]), parseDate(dateStrings[dateStrings.length - 1])) === (dateStrings.length - 1)) {
    // These can be formatted in a simple way
    formattedDateRange = `${dateFormatter(dateStrings[0])}-${dateFormatter(dateStrings[dateStrings.length - 1])}`;
  } else {
    formattedDateRange = dateStrings.map(dateFormatter).join(',');
  }
  return formattedDateRange;
}

function durationFormat(minutes) {
  return `${minutes >= 60 ? `${Math.floor(minutes / 60)} h ` : ''}${minutes % 60} min`;
}

const defaultWeek = [0, 1, 2, 3, 4, 5, 6];
const defaultState = Object.freeze({
  dateNumberFormat: d => timeFormat('%u')(d) % 7,
  dayTimeFormat,
  durationFormat,
  formatDate: dateFormat,
  formatDateRange,
  formatDateString,
  formatGlucose,
  formatGlucoseUnits,
  formatGlucoseValue,
  locale: initialLocale,
  longDateFormat: timeFormat('%A %x'),
  parseDate,
  setLocale: f => undefined,
  setUnits: f => undefined,
  shortHour: 'h',
  toggleUnits: f => undefined,
  units: defaultUnits,
  week: defaultWeek,
  weekDays: defaultWeek.map(d => dateNames[d]),
  weekInterval: timeMonday,
  yearAndWeekNumberFormat: f => undefined,
});

const PresentationContext = React.createContext(defaultState);

function getWeekNumberDefinition(locale) {
  if ((locale.indexOf('-US') > 0) || (locale.indexOf('-CA') > 0)) {
    // According to Wikipedia, the USA, Canada, and Japan consider Sunday as the first day of the week.
    // We ignore Japan, until we have a localization for Japanese.
    // https://en.wikipedia.org/wiki/Week
    return '%U';
  }
  return '%V';
}

export function PresentationContextProvider({ children }) {
  const { i18n, t } = useTranslation('timeFormat');
  const [ locale, setLocale ] = useState(initialLocale);
  const [ timeDefinition, setTimeDefinition ] = useState(initialLocaleDefinition);
  const [ units, setUnits ] = useState(locale.indexOf('-US') > 0 ? mgdL : mmolL);

  function checkAndSetLocale(newLocale) {
    if (newLocale && newLocale !== locale) {
      i18n.changeLanguage(newLocale)
      .then(() => {
        setLocale(newLocale);
      })
      .catch(console.error);
    }
  }

  function toggleUnits() {
    setUnits(units === mmolL ? mgdL : mmolL);
  }

  const localizedTimeFormat = useRef({ format: timeFormat });

  useEffect(() => {
    const definition = t('definition', { returnObjects: true });
    localizedTimeFormat.current = timeFormatDefaultLocale(definition);
    setTimeDefinition(definition);
  }, [locale, t]);

  const weekNumberDefinition = getWeekNumberDefinition(locale);
  const dateNumberDefinition = localizedTimeFormat.current.format(weekNumberDefinition === '%U' ? '%w' : '%u');
  const dateNumberFormat = d => dateNumberDefinition(d) % 7;
  const week = weekNumberDefinition === '%U' ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 0];
  const weekInterval = weekNumberDefinition === '%U' ? timeSunday : timeMonday;
  const weekDays = week.map(d => dateNames[d]);

  function yearAndWeekNumberFormat(date) {
    // Get the week number of the first day of the week.
    let d = weekInterval(date);
    const week = timeFormat(weekNumberDefinition)(d);
    const month = d.getMonth();
    const yearFormat = timeFormat('%Y');
    let year = yearFormat(d);
    if (+week < month) {
      // This is already a week of the next year,
      // let's format a day from the next year instead (the last day of the week)
      d = timeDay.offset(d, 6);
      year = yearFormat(d);
    } else if ((month === 0) && (+week > 51)) {
      // still a week of the previous year, let's format that year instead
      d = timeDay.offset(d, -6);
      year = yearFormat(d);
    }
    return `${year}-${week}`;
  }

  const durationFormat = (minutes) => {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const reminderMinutes = minutes % 60;
    if (days) {
      return `${days}${t('shortDay')} ${hours}${t('shortHour')} ${reminderMinutes}${t('shortMinute')}`
    } else if (hours) {
      return `${hours}${t('shortHour')} ${reminderMinutes}${t('shortMinute')}`
    } else if (minutes) {
      return `${reminderMinutes}${t('shortMinute')}`;
    }
    return(`0${t('shortMinute')}`)
  }

  const localizedDateStringFormatter = d => localizedTimeFormat.current.format('%x')(parseDate(d));
  const formatDate = localizedTimeFormat.current.format(locale.indexOf('en') === 0
  ? '%x'
  : '%-d.%-m.%Y');
  const formatHour = localizedTimeFormat.current.format(locale.indexOf('en') === 0
  ? '%-I %p'
  : '%-H');

  const formatTime = localizedTimeFormat.current.format(timeDefinition.clockFormat);
  const formatTimeAndDate = localizedTimeFormat.current.format(timeDefinition.shortDateTime);

  const state = {
    ...defaultState,
    dateNumberFormat,
    durationFormat,
    formatDate,
    formatDateString: localizedDateStringFormatter,
    formatHour,
    formatDateRange: days => formatDateRange(days, localizedDateStringFormatter),
    formatGlucose: (g, u = units) => formatGlucose(g, u),
    formatGlucoseValue: (g, u = units) => formatGlucoseValue(g, u),
    formatTimeAndDate,
    formatTimeString: formatTime,
    fullHourFormat: timeDefinition.fullHourFormat,
    locale,
    longDateFormat: localizedTimeFormat.current.format('%A %x'),
    setLocale: checkAndSetLocale,
    setUnits,
    shortHour: t('shortHour'),
    timeFormat: localizedTimeFormat.current.format,
    toggleUnits,
    units,
    week,
    weekDays,
    weekInterval,
    yearAndWeekNumberFormat,
  };

  return (
    <PresentationContext.Provider value={state}>
      { children }
    </PresentationContext.Provider>
  );
}

export default PresentationContext;
